import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PortableText } from "@portabletext/react";
import { Reveal, RevealImage, MaskedLines } from "@/lib/motion";
import { Page, Overline, CTABand } from "@/components/shared";
import { useContent, usePost } from "@/lib/ContentProvider";
import { imgSrc } from "@/lib/sanity";

const ptComponents = {
  types: {
    image: ({ value }) => (
      <img
        className="my-6 rounded-2xl w-full h-auto"
        src={imgSrc(value, 1200) || ""}
        alt={value?.alt || ""}
        loading="lazy"
      />
    ),
  },
  block: {
    h2: ({ children }) => <h2 className="mt-10 font-serif text-3xl text-alch-forest">{children}</h2>,
    h3: ({ children }) => <h3 className="mt-8 font-serif text-2xl text-alch-forest">{children}</h3>,
    normal: ({ children }) => <p className="text-lg leading-relaxed text-alch-ink/85">{children}</p>,
    blockquote: ({ children }) => <blockquote className="my-6 border-l-2 border-alch-terracotta pl-6 font-serif italic text-xl text-alch-forest">{children}</blockquote>,
  },
  list: {
    bullet: ({ children }) => <ul className="list-disc pl-6 space-y-2 text-alch-ink/85">{children}</ul>,
    number: ({ children }) => <ol className="list-decimal pl-6 space-y-2 text-alch-ink/85">{children}</ol>,
  },
  marks: {
    link: ({ children, value }) => (
      <a className="text-alch-terracotta underline" href={value?.href} target="_blank" rel="noreferrer">{children}</a>
    ),
    strong: ({ children }) => <strong className="text-alch-forest">{children}</strong>,
  },
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = usePost(slug);
  const { posts } = useContent();

  if (!post) {
    return (
      <Page testid="blog-post-missing">
        <div className="mx-auto max-w-3xl px-5 pt-48 pb-32 text-center">
          <h1 className="font-serif text-4xl text-alch-forest">Article not found</h1>
          <Link to="/blog" className="mt-6 inline-block text-alch-terracotta underline">Back to blog</Link>
        </div>
      </Page>
    );
  }

  const related = posts.filter((b) => b.slug !== slug).slice(0, 2);
  const isPortableText = Array.isArray(post.body);

  return (
    <Page testid="blog-post-page">
      <section className="relative overflow-hidden bg-alch-forest px-5 pt-40 pb-20 text-alch-bg md:px-8 md:pt-48">
        <div className="mx-auto max-w-4xl">
          <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-sm text-alch-bg/70 transition-colors hover:text-alch-bg">
            <ArrowLeft className="h-4 w-4" /> All articles
          </Link>
          <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-[0.16em] text-alch-terracotta">
            <span>{post.tag}</span><span className="text-alch-bg/50">· {post.date} · {post.read}</span>
          </div>
          <MaskedLines as="h1" lines={[post.title]} delay={0.15} className="mt-5 font-serif text-4xl leading-tight md:text-6xl" />
        </div>
      </section>

      <article className="px-5 py-20 md:px-8">
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <RevealImage src={post.image} alt={post.title} className="h-72 rounded-[2rem] md:h-[26rem]" />
          </Reveal>
          <div className="mx-auto mt-14 max-w-2xl space-y-6">
            {isPortableText ? (
              <PortableText value={post.body} components={ptComponents} />
            ) : Array.isArray(post.body) ? (
              post.body.map((p, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <p className={`leading-relaxed text-alch-ink/85 ${i === 0 ? "font-serif text-2xl leading-snug text-alch-forest" : "text-lg"}`}>{p}</p>
                </Reveal>
              ))
            ) : (
              <p className="text-lg text-alch-ink/80">{post.excerpt}</p>
            )}
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="bg-alch-surface px-5 py-20 md:px-8">
          <div className="mx-auto max-w-7xl">
            <Overline>Keep reading</Overline>
            <div className="mt-8 grid gap-8 md:grid-cols-2">
              {related.map((b, i) => (
                <Reveal key={b.slug} delay={i * 0.08}>
                  <Link to={`/blog/${b.slug}`} className="group grid grid-cols-[120px_1fr] items-center gap-5 rounded-[1.5rem] border border-alch-forest/10 bg-alch-bg p-4">
                    <div className="overflow-hidden rounded-2xl">
                      <RevealImage src={b.image} alt={b.title} className="h-24" imgClassName="transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div>
                      <span className="text-xs uppercase tracking-[0.14em] text-alch-terracotta">{b.tag}</span>
                      <h3 className="mt-1 font-serif text-xl leading-snug text-alch-forest">{b.title}</h3>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTABand />
    </Page>
  );
}
